import React, { useEffect, useState } from 'react';
import { AlertTriangle, ShoppingBag } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import DeliveryAddressModal from './DeliveryAddressModal';
import FoodOrdersList from './FoodOrdersList';
import ProductDetailModal from './ProductDetailModal';
import RestaurantCart from './RestaurantCart';
import RestaurantCheckout from './RestaurantCheckout';
import RestaurantDetail from './RestaurantDetail';
import { RestaurantToast } from './RestaurantFeedback';
import RestaurantHeader from './RestaurantHeader';
import RestaurantHome from './RestaurantHome';
import RestaurantMobileNav from './RestaurantMobileNav';
import RestaurantOverlay from './RestaurantOverlay';
import RestaurantProfile from './RestaurantProfile';
import useRestaurantClient, { useRestaurantDensity } from './useRestaurantClient';
import { cartCount } from './restaurantUtils';
import './restaurantClient.css';

export default function RestaurantClientShell({ onBack, onOrderCreated }) {
  const { user } = useAuth();
  const client = useRestaurantClient({ onOrderCreated });
  const [density, setDensity] = useRestaurantDensity();
  const [tab, setTab] = useState('home');
  const [query, setQuery] = useState('');
  const [product, setProduct] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [addressOpen, setAddressOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [client.selectedRestaurant?.id, tab]);

  const goToTab = (next) => {
    setTab(next);
    if (next === 'home' || next === 'promos') {
      client.closeRestaurant();
    }
  };

  const handleProductAdd = (item, quantity, observation) => {
    if (item.cartItem) {
      client.updateCartItem(item.cartItem.line_id, item, quantity, observation);
    } else {
      client.addProduct(item, quantity, observation);
    }
    setProduct(null);
  };

  const handleSubmit = async () => {
    const order = await client.submitOrder();
    if (order) {
      setCheckoutOpen(false);
      setCartOpen(false);
      setTab('orders');
    }
  };

  return (
    <div className={`rc-shell rc-shell--${density}`} data-testid="restaurant-marketplace">
      <RestaurantHeader
        address={client.address}
        cart={client.cart}
        density={density}
        onDensityChange={setDensity}
        onBack={onBack}
        onAddress={() => setAddressOpen(true)}
        onCart={() => setCartOpen(true)}
        query={query}
        onQueryChange={setQuery}
        user={user}
      />
      <nav className="rc-desktop-nav" aria-label="Secciones del módulo">
        {[
          ['home', 'Restaurantes'],
          ['orders', 'Mis pedidos'],
          ['profile', 'Mi perfil']
        ].map(([id, label]) => (
          <button type="button" key={id} className={tab === id ? 'is-active' : ''} onClick={() => goToTab(id)}>{label}</button>
        ))}
      </nav>

      <main className="rc-main">
        {(tab === 'home' || tab === 'promos') && !client.selectedRestaurant && (
          <RestaurantHome
            restaurants={tab === 'promos'
              ? client.restaurants.filter((restaurant) => restaurant.promo_text)
              : client.restaurants}
            loading={client.restaurantsLoading}
            error={client.restaurantsError}
            query={query}
            onRetry={client.loadRestaurants}
            onSelect={client.openRestaurant}
          />
        )}
        {(tab === 'home' || tab === 'promos') && client.selectedRestaurant && (
          <RestaurantDetail
            restaurant={client.selectedRestaurant}
            menu={client.menu}
            menuLoading={client.menuLoading}
            menuError={client.menuError}
            query={query}
            cart={client.cart}
            onBack={client.closeRestaurant}
            onRetry={client.loadMenu}
            onOpenProduct={setProduct}
            onQuickAdd={(item) => client.addProduct(item)}
          />
        )}
        {tab === 'orders' && (
          <FoodOrdersList
            orders={client.orders}
            loading={client.ordersLoading}
            error={client.ordersError}
            onRetry={client.loadOrders}
            selectedOrderId={client.selectedOrderId}
            onSelectOrder={client.setSelectedOrderId}
            onRefreshOrder={client.refreshOrders}
            lastUpdatedAt={client.lastOrdersUpdatedAt}
          />
        )}
        {tab === 'profile' && <RestaurantProfile user={user} />}
      </main>

      {client.cart.length > 0 && !cartOpen && !checkoutOpen && (
        <button type="button" className="rc-floating-cart" onClick={() => setCartOpen(true)}>
          <ShoppingBag aria-hidden="true" />
          <span>Ver carrito</span>
          <b>{cartCount(client.cart)}</b>
        </button>
      )}
      <RestaurantMobileNav active={tab} onChange={goToTab} />

      {cartOpen && (
        <RestaurantCart
          restaurant={client.selectedRestaurant}
          cart={client.cart}
          onClose={() => setCartOpen(false)}
          onQuantity={client.changeQuantity}
          onEdit={(item) => {
            const menuProduct = client.menu.items.find((entry) => entry.id === item.menu_item_id);
            if (menuProduct) setProduct({ ...menuProduct, cartItem: item });
          }}
          onCheckout={() => {
            setCartOpen(false);
            setCheckoutOpen(true);
          }}
        />
      )}
      {checkoutOpen && (
        <RestaurantCheckout
          cart={client.cart}
          address={client.address}
          notes={client.notes}
          submitting={client.submitting}
          error={client.submitError}
          onNotesChange={client.setNotes}
          onAddress={() => setAddressOpen(true)}
          onClose={() => setCheckoutOpen(false)}
          onSubmit={handleSubmit}
        />
      )}
      {product && (
        <ProductDetailModal product={product} onClose={() => setProduct(null)} onAdd={handleProductAdd} />
      )}
      {addressOpen && (
        <DeliveryAddressModal
          restaurant={client.selectedRestaurant}
          address={client.address}
          onClose={() => setAddressOpen(false)}
          onSave={(next) => {
            client.saveAddress(next);
            setAddressOpen(false);
          }}
        />
      )}
      {client.pendingRestaurant && (
        <RestaurantOverlay title="Cambiar de restaurante" onClose={() => client.setPendingRestaurant(null)} className="rc-confirm-dialog">
          <span className="rc-confirm-dialog__icon"><AlertTriangle aria-hidden="true" /></span>
          <h2>Tu carrito pertenece a otro restaurante</h2>
          <p>Para pedir en {client.pendingRestaurant.name} debes vaciar el carrito actual. No se eliminará nada hasta que confirmes.</p>
          <div className="rc-modal-actions">
            <button type="button" className="rc-button rc-button--secondary" onClick={() => client.setPendingRestaurant(null)}>Conservar carrito</button>
            <button type="button" className="rc-button rc-button--danger" onClick={client.confirmRestaurantChange}>Vaciar y continuar</button>
          </div>
        </RestaurantOverlay>
      )}
      <RestaurantToast toast={client.toast} onClose={() => client.setToast(null)} />
    </div>
  );
}
